import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';

import 'style/layout/Forms.css';

const hasOnlyZeros = (value: string) => /^0*$/.test(value);

const NumberInputField = ({
  className,
  disabled,
  error,
  id,
  label,
  name,
  onChange,
  required,
  value,
}: {
  className?: string;
  disabled?: boolean;
  error?: string;
  id: string;
  label?: string;
  name?: string;
  onChange?: any;
  required?: boolean;
  value: number;
}): JSX.Element => {
  const [displayValue, setDisplayValue] = useState(value?.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const currentInputRef = inputRef.current;

    const keydownEvent = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.stopPropagation();
      }
    };

    currentInputRef?.removeEventListener('keydown', keydownEvent);
    currentInputRef?.addEventListener('keydown', keydownEvent);

    return () => {
      currentInputRef?.removeEventListener('keydown', keydownEvent);
    };
  });

  useEffect(() => {
    const displayValueIsZero =
      displayValue === '' || hasOnlyZeros(displayValue);

    if (value === 0 && displayValueIsZero) {
      return;
    }
    if (value?.toString() !== displayValue) {
      onDisplayValueChange(value.toString());
    }
  }, [value]);

  const onDisplayValueChange = (newValue: string) => {
    if (onChange === undefined) {
      return;
    }

    setDisplayValue(newValue);

    if (newValue === '' || Number.isNaN(Number(newValue))) {
      onChange({
        target: { name, value: 0 },
      });

      return;
    }

    onChange({
      target: {
        name,
        value: parseInt(newValue, 10),
      },
    });
  };

  const handleOnChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) =>
    onDisplayValueChange(event.target.value);

  const getErrorMessage = () => {
    if (error === undefined) {
      return null;
    }

    return <span className="input-error-message">{error}</span>;
  };

  return (
    <FormGroup>
      {label === undefined ? null : (
        <Label className="label" for={id}>
          {label}
        </Label>
      )}
      <Input
        className={`text-field ${className}`}
        disabled={disabled}
        id={id}
        innerRef={inputRef}
        name={name}
        required={required === undefined ? false : required}
        onChange={handleOnChangeEvent}
        value={displayValue}
        type="text"
      />
      {getErrorMessage()}
    </FormGroup>
  );
};

export default NumberInputField;
